'use client';

import {
	RemoteConfig,
	getRemoteConfig,
	fetchAndActivate,
	getBoolean,
} from 'firebase/remote-config';
import type { FirebaseApp } from 'firebase/app';
import { logger } from '@/core/logger';

interface IRemoteConfigService {
	initialize(app: FirebaseApp): Promise<boolean>;
	getBoolean(key: string): boolean;
}

// Remote Config parameter keys
export const REMOTE_CONFIG_KEYS = {
	IS_TEAM_MANAGEMENT_ENABLED: 'is_team_management_enabled',
	IS_PROJECT_MANAGEMENT_ENABLED: 'is_project_management_enabled',
	IS_PAYMENT_ENABLED: 'is_payment_enabled',
	IS_BYOM_ENABLED: 'is_byom_enabled',
	IS_SEEDANCE_2_ENABLED: 'is_seedance_2_enabled',
	IS_ADMIN_PORTAL_ENABLED: 'is_admin_portal_enabled',
	IS_BILLING_TRANSPARENCY_ENABLED: 'is_billing_transparency_enabled',
	IS_SCENE_BUILDER_ENABLED: 'is_scene_builder_enabled',
	IS_CHARACTER_REGISTRATION_ENABLED: 'is_character_registration_enabled',
	IS_4K_ENABLED: 'is_4k_enabled',
	IS_VOICE_REFERENCE_ENABLED: 'is_voice_reference_enabled',
	IS_INTERACTIVE_EDITING_ENABLED: 'is_interactive_editing_enabled',
	IS_LAYER_DECOMPOSITION_ENABLED: 'is_layer_decomposition_enabled',
};

// Default values (fallbacks)
const DEFAULT_CONFIG_VALUES = {
	[REMOTE_CONFIG_KEYS.IS_TEAM_MANAGEMENT_ENABLED]: false,
	[REMOTE_CONFIG_KEYS.IS_PROJECT_MANAGEMENT_ENABLED]: false,
	// !!! LOCAL TESTING ONLY — revert IS_PAYMENT_ENABLED + IS_BILLING_TRANSPARENCY_ENABLED to false before committing !!!
	[REMOTE_CONFIG_KEYS.IS_PAYMENT_ENABLED]: true,
	[REMOTE_CONFIG_KEYS.IS_BYOM_ENABLED]: false,
	[REMOTE_CONFIG_KEYS.IS_SEEDANCE_2_ENABLED]: true,
	[REMOTE_CONFIG_KEYS.IS_ADMIN_PORTAL_ENABLED]: false,
	[REMOTE_CONFIG_KEYS.IS_BILLING_TRANSPARENCY_ENABLED]: true,
	[REMOTE_CONFIG_KEYS.IS_SCENE_BUILDER_ENABLED]: false,
	// Off until rollout; mirrors backend character.registration.enabled (TSO-694).
	[REMOTE_CONFIG_KEYS.IS_CHARACTER_REGISTRATION_ENABLED]: false,
	// Off until rollout; gates Seedance 4K resolution (Studio/Brand only, TSO-738).
	[REMOTE_CONFIG_KEYS.IS_4K_ENABLED]: false,
	// Off until rollout; gates the Seedance 2.0 "Voice only" control (TSO-756).
	[REMOTE_CONFIG_KEYS.IS_VOICE_REFERENCE_ENABLED]: false,
	// Off until rollout; gates Seedream 5.0-pro interactive editing — the
	// annotate entry point AND the badge resolution at submit. Both, so that
	// with the flag off the behaviour is byte-for-byte what it was before the
	// feature existed.
	[REMOTE_CONFIG_KEYS.IS_INTERACTIVE_EDITING_ENABLED]: false,
	// Defaults FALSE everywhere: the backend flat-price slice must deploy first,
	// or a layer request generates an ordinary image and charges for it.
	[REMOTE_CONFIG_KEYS.IS_LAYER_DECOMPOSITION_ENABLED]: false,
};

class FirebaseRemoteConfigService implements IRemoteConfigService {
	private initialized: boolean = false;
	private remoteConfig: RemoteConfig | null = null;

	async initialize(app: FirebaseApp): Promise<boolean> {
		// Prevent re-initialization
		if (this.initialized) {
			return true;
		}

		try {
			// Initialize Remote Config with provided app
			this.remoteConfig = getRemoteConfig(app);

			if (this.remoteConfig) {
				// Control how stale a cached config may be before a refetch. The
				// Firebase SDK default is 12h, which means a published flag change
				// (e.g. a feature rollout) does NOT reach deployed clients for up to
				// 12h — they keep serving the pre-publish cache and read code-side
				// defaults. That silently broke the is_character_registration_enabled
				// rollout (TSO-699): the flag was true in console but every deployed
				// client read false. Use 1 min locally and 1h in deployed envs so a
				// client picks up a published flag change on its next load within the
				// hour (instead of up to 12h), while staying well under Firebase's
				// fetch-throttle ceiling. Note: fetchAndActivate runs once at init, so
				// a tab left open across a publish still won't refresh until reload —
				// active-tab propagation (visibility refetch / onConfigUpdate) is a
				// follow-up (TSO-702).
				this.remoteConfig.settings.minimumFetchIntervalMillis =
					process.env.NODE_ENV === 'development' ? 60_000 : 3_600_000;

				// Set default config values before fetching
				this.remoteConfig.defaultConfig = DEFAULT_CONFIG_VALUES;

				this.initialized = true;

				// Automatically fetch config after initialization
				try {
					await fetchAndActivate(this.remoteConfig);
				} catch (error) {
					logger.warn(
						'Failed to fetch Remote Config on initialization',
						error
					);
				}

				return true;
			}

			return false;
		} catch (error) {
			logger.error('Failed to initialize Firebase Remote Config', error);
			return false;
		}
	}

	getBoolean(key: string): boolean {
		if (!this.remoteConfig || !this.initialized) {
			const defaultValue =
				DEFAULT_CONFIG_VALUES[
					key as keyof typeof DEFAULT_CONFIG_VALUES
				];
			return typeof defaultValue === 'boolean' ? defaultValue : false;
		}

		try {
			return getBoolean(this.remoteConfig, key);
		} catch (error) {
			logger.error(`Error getting boolean value: ${key}`, error);
			const defaultValue =
				DEFAULT_CONFIG_VALUES[
					key as keyof typeof DEFAULT_CONFIG_VALUES
				];
			return typeof defaultValue === 'boolean' ? defaultValue : false;
		}
	}
}

export const firebaseRemoteConfigService = new FirebaseRemoteConfigService();
